import React, {
    useState, useRef, useImperativeHandle, forwardRef, useEffect,
} from 'react';
import { Box } from '@mui/material';
import RazProgressSVG from '../../../../components/UI/SVG_IMGs/RazProgressSVG';
import PoweredByDQDefault from '../../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQWhite from '../../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../../context/colorMode/colorMode.context';
import useWidgetStyles from '../../../../assets/styles/CpaFloatingChatWidgetStyles';

/**
 * Floating chat widget for /pbdq/apply.
 *
 * This is a faithful re-implementation of the program-creation preview's
 * floating chat bubble (button → animated panel reveal → collapse) so the look
 * and motion match exactly. It is intentionally self-contained (own styles +
 * animation state) so it does NOT touch the shared DQProV2 page.
 *
 * The panel BODY is supplied via `children` — here it hosts the existing
 * Curation Prompt Application chat flow.
 *
 * Imperative API (via ref): { open(), close() } so the parent can open the chat
 * when the user triggers the flow from the main content area.
 */

const CpaFloatingChatWidget = forwardRef(({ children, startOpen = false, hidden = false }, ref) => {
    const classes = useWidgetStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ = isDark ? PoweredByDQWhite : PoweredByDQDefault;
    const [isChatOpen, setIsChatOpen] = useState(false);        // controls DOM mount
    const [chatAnimPhase, setChatAnimPhase] = useState('idle'); // drives the BUTTON animation
    const [panelPhase, setPanelPhase] = useState('idle');       // drives the PANEL clip-path

    const chatTimersRef = useRef([]);
    const chatOpenRafRef = useRef(null);
    const panelRef = useRef(null);

    const clearChatTimers = () => {
        chatTimersRef.current.forEach((t) => clearTimeout(t));
        chatTimersRef.current = [];
        if (chatOpenRafRef.current) { cancelAnimationFrame(chatOpenRafRef.current); chatOpenRafRef.current = null; }
    };

    // Inline styles for the toggle bubble at each animation phase (mirrors the
    // program-creation widget's getBubbleStyle).
    const getBubbleStyle = () => {
        const PANEL_W = 400;
        const BTN = 56;
        const HDR = 67;
        const ICON_FROM_LEFT = 35;
        const actualPanelH = Math.min(window.innerHeight - 48, 720);
        const riseY = Math.max(0, actualPanelH - HDR / 2 - BTN / 2);
        const slideX = (24 + PANEL_W - ICON_FROM_LEFT) - (24 + BTN / 2);
        switch (chatAnimPhase) {
            case 'pre-open':
                return { pointerEvents: 'none', transition: 'none' };
            case 'opening':
                return {
                    transform: `translateY(-${riseY}px)`,
                    transition: 'transform 1.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: 1,
                    pointerEvents: 'none',
                    zIndex: 1302,
                };
            case 'sliding':
                return {
                    transform: `translateY(-${riseY}px) translateX(-${slideX}px) scale(0.68)`,
                    transition: 'transform 0.55s ease, opacity 0.45s ease',
                    opacity: 0,
                    pointerEvents: 'none',
                    zIndex: 1302,
                };
            case 'open':
                return { transform: 'scale(0)', opacity: 0, pointerEvents: 'none', transition: 'none' };
            default:
                return {};
        }
    };

    const openChat = () => {
        clearChatTimers();
        setIsChatOpen(true);
        setPanelPhase('pre-open');
        setChatAnimPhase('pre-open');
        chatOpenRafRef.current = requestAnimationFrame(() => {
            chatOpenRafRef.current = requestAnimationFrame(() => {
                setPanelPhase('opening');
                setChatAnimPhase('opening');
                chatOpenRafRef.current = null;
                const t1 = setTimeout(() => {
                    setPanelPhase('sliding');
                    setChatAnimPhase('sliding');
                    const t2 = setTimeout(() => {
                        setPanelPhase('open');
                        setChatAnimPhase('open');
                    }, 550);
                    chatTimersRef.current.push(t2);
                }, 1300);
                chatTimersRef.current.push(t1);
            });
        });
    };

    const closeChat = () => {
        clearChatTimers();
        setPanelPhase('closing');
        setChatAnimPhase('idle');
        const t = setTimeout(() => { setIsChatOpen(false); setPanelPhase('idle'); }, 1600);
        chatTimersRef.current.push(t);
    };

    useImperativeHandle(ref, () => ({
        open: () => { if (!isChatOpen) openChat(); },
        close: () => { if (isChatOpen) closeChat(); },
        isOpen: () => isChatOpen,
    }));

    // Optionally open on mount.
    useEffect(() => {
        if (startOpen) openChat();
        return () => clearChatTimers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const showContent = panelPhase === 'open' || panelPhase === 'closing';

    return (
        <>
            {isChatOpen && (
                <Box
                    ref={panelRef}
                    className={`${classes.chatFloatingPanel}${panelPhase !== 'idle' ? ` phase-${panelPhase}` : ''}`}
                    style={hidden ? { display: 'none' } : undefined}
                >
                    <Box className={classes.header}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Box className={classes.brandIcon}>
                                <RazProgressSVG color="#FF6900" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 0 1.5px #FF6900)' }} />
                            </Box>
                            <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--brand-text, #111)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>DQ AI</span>
                                <span style={{ fontSize: '12px', color: '#999', lineHeight: 1.3 }}>Here to help you</span>
                            </Box>
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, marginLeft: '12px' }}>
                            <img src={PoweredByDQ} alt="Powered by DQ" style={{ height: '32px', flexShrink: 0 }} />
                            <Box
                                onClick={closeChat}
                                style={{
                                    width: '28px', height: '28px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', background: 'var(--brand-surface, #F3F4F6)', flexShrink: 0,
                                    fontSize: '14px', color: '#FF6900', fontWeight: 700,
                                    transition: 'background 0.15s',
                                }}
                                title="Minimise chat"
                            >
                                −
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        className={classes.body}
                        sx={{ opacity: showContent ? 1 : 0, transition: showContent ? 'opacity 0.8s ease 0.4s' : 'none' }}
                    >
                        {children}
                    </Box>
                </Box>
            )}

            <Box
                className={`${classes.chatToggleBtn}${chatAnimPhase === 'idle' ? ' btn-idle' : ''}`}
                style={{ ...getBubbleStyle(), ...(hidden ? { display: 'none' } : {}) }}
                onClick={openChat}
            >
                <RazProgressSVG color="#FF6900" style={{ width: '80%', height: '80%', filter: 'drop-shadow(0 0 1.5px #FF6900)' }} />
            </Box>
        </>
    );
});

CpaFloatingChatWidget.displayName = 'CpaFloatingChatWidget';

export default CpaFloatingChatWidget;
